import { useEffect, useState, type FormEvent } from "react";
import { Button, Field, EmptyState, Spinner } from "@cntrl/ui";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { useOrg, useUpdateOrg, useSetOrgLogo, useClearOrgLogo, orgTypeLabel, orgInitials } from "../../app/hooks/useOrg.js";
import { AvatarEditor } from "../shared/AvatarEditor.js";
import styles from "./OrgProfileForm.module.scss";

/**
 * The org profile editor (shared by OrgProfileModal + OrgSettingsScreen). `GET /orgs/{orgId}` preload →
 * `PUT /orgs/{orgId}` (name / contact email / logo). `orgType` is org identity and IMMUTABLE post-create
 * so it renders READ-ONLY. Loading / error / 404 states are designed; a save surfaces the
 * server error verbatim inline and never reports success on an unpersisted write.
 */
export function OrgProfileForm({ orgId, onSaved }: { orgId: string; onSaved?: () => void }) {
  const q = useOrg(orgId);
  const update = useUpdateOrg(orgId);
  const setLogo = useSetOrgLogo(orgId);
  const clearLogo = useClearOrgLogo(orgId);

  const [form, setForm] = useState({ name: "", contactEmail: "" });
  const [logoMediaId, setLogoMediaId] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);

  // The org's logo via the tenant-less global avatar route — the one identity-picture path (an org logo is
  // looked at from outside the org: the topbar chip, the Teams card, a link handshake). Resolves nothing
  // until a logo is set, which renders the initials fallback.
  const logoUrl = useAvatarUrl("org", orgId, logoMediaId);

  useEffect(() => {
    if (hydrated || !q.data) return;
    setForm({ name: q.data.name ?? "", contactEmail: q.data.contactEmail ?? "" });
    setLogoMediaId(q.data.logoMediaId ?? null);
    setHydrated(true);
  }, [hydrated, q.data]);

  if (q.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={22} label="Loading organization…" />
      </div>
    );
  }
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return notFound ? (
      <EmptyState title="Organization not found" description="This organization isn't available or has been removed." />
    ) : (
      <EmptyState
        title="Couldn't load this organization"
        description={q.error instanceof Error ? q.error.message : "Please try again."}
        action={<Button onClick={() => void q.refetch()}>Retry</Button>}
      />
    );
  }

  const org = q.data;
  const nameValid = form.name.trim() !== "";

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (!nameValid) {
      setError("Enter the organization name.");
      return;
    }
    update.mutate(
      {
        name: form.name.trim(),
        contactEmail: form.contactEmail.trim() || null,
        // orgType is NOT sent — it is immutable post-create. logoMediaId round-trips the CURRENT logo: the
        // logo uploads on its own (below) and writes this state back, so a Save after an upload never sends
        // the stale id and clobbers it.
        logoMediaId,
      },
      {
        onSuccess: () => {
          setDone(true);
          onSaved?.();
        },
        onError: (err) => setError(err.message),
      },
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      {/* The logo uploads IMMEDIATELY (AvatarEditor's rule), squared by the crop step; it is what the roster
          rail's All-roster tile shows in place of its four-colour default (ART-337). */}
      <AvatarEditor
        src={logoUrl}
        placeholder={orgInitials(form.name || org?.name)}
        shape="rounded"
        label={logoMediaId ? "Replace organization logo" : "Upload organization logo"}
        removeLabel="Remove organization logo"
        busy={setLogo.isPending || clearLogo.isPending}
        onPicked={(file) => {
          setLogoError(null);
          setLogo.mutate(file, {
            onSuccess: (data) => setLogoMediaId(data.logoMediaId ?? null),
            onError: (err) => setLogoError(errorMessage(err, "Couldn't upload that logo.")),
          });
        }}
        onRemove={
          logoMediaId
            ? () => {
                setLogoError(null);
                clearLogo.mutate(undefined, {
                  onSuccess: () => setLogoMediaId(null),
                  onError: (err) => setLogoError(errorMessage(err, "Couldn't remove the logo.")),
                });
              }
            : undefined
        }
      />
      {logoError && (
        <p className={styles.error} role="alert">
          {logoError}
        </p>
      )}
      <p className={styles.logoHint}>A square logo works best. It shows on your roster.</p>

      <div className={styles.identity}>
        <div>
          <p className={styles.typeLabel}>{orgTypeLabel(org?.orgType)}</p>
          <p className={styles.typeHint}>Organization type can't be changed after creation.</p>
        </div>
      </div>

      <Field label="Organization name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      <Field
        label="Contact email"
        type="email"
        placeholder="team@org.com"
        value={form.contactEmail}
        onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
      />

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {done && !error && (
        <p className={styles.success} role="status">
          Saved.
        </p>
      )}

      <div className={styles.actions}>
        <Button type="submit" loading={update.isPending} disabled={!nameValid}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
